<template>
  <div class="space-y-4">
    <!-- Filter bar -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
      <div class="sm:col-span-2 xl:col-span-2">
        <label class="block text-[11px] font-medium text-gray-500 mb-1">Search</label>
        <IconField iconPosition="left" class="w-full">
          <InputIcon class="pi pi-search text-gray-400" />
          <InputText
            v-model="filters.search"
            size="small"
            placeholder="Tracking code, name, email..."
            class="w-full rounded-lg"
            data-testid="transcript-search-input"
            @input="onFilterChanged"
          />
        </IconField>
      </div>
      <div>
        <label class="block text-[11px] font-medium text-gray-500 mb-1">Status</label>
        <Select
          v-model="filters.status"
          :options="statusOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="All statuses"
          size="small"
          showClear
          class="w-full rounded-lg"
          data-testid="transcript-status-filter"
          @change="onFilterChanged"
        />
      </div>
      <div>
        <label class="block text-[11px] font-medium text-gray-500 mb-1">Type</label>
        <Select
          v-model="filters.transcript_type_id"
          :options="typeOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="All types"
          size="small"
          showClear
          class="w-full rounded-lg"
          data-testid="transcript-type-filter"
          @change="onFilterChanged"
        />
      </div>
      <div>
        <label class="block text-[11px] font-medium text-gray-500 mb-1">Applicant Category</label>
        <Select
          v-model="filters.applicant_category"
          :options="categoryOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="All categories"
          size="small"
          showClear
          class="w-full rounded-lg"
          data-testid="transcript-category-filter"
          @change="onFilterChanged"
        />
      </div>
    </div>
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      <div>
        <label class="block text-[11px] font-medium text-gray-500 mb-1">Date From</label>
        <DatePicker
          v-model="dateFrom"
          dateFormat="yy-mm-dd"
          showIcon
          showButtonBar
          size="small"
          placeholder="Start date"
          class="w-full rounded-lg"
          data-testid="transcript-date-from"
          @date-select="onFilterChanged"
          @clear-click="onFilterChanged"
        />
      </div>
      <div>
        <label class="block text-[11px] font-medium text-gray-500 mb-1">Date To</label>
        <DatePicker
          v-model="dateTo"
          dateFormat="yy-mm-dd"
          showIcon
          showButtonBar
          size="small"
          placeholder="End date"
          class="w-full rounded-lg"
          data-testid="transcript-date-to"
          @date-select="onFilterChanged"
          @clear-click="onFilterChanged"
        />
      </div>
    </div>

    <!-- Data table -->
    <div class="bg-white rounded-lg shadow">
      <DataTable
        size="small"
        :value="requests"
        :loading="loading"
        :rows="perPage"
        :totalRecords="total"
        :first="(currentPage - 1) * perPage"
        paginator
        lazy
        stripedRows
        responsiveLayout="scroll"
        :rowsPerPageOptions="[10, 15, 25, 50]"
        dataKey="id"
        @page="onPage"
      >
        <template #empty>
          <div class="text-center py-6 text-gray-400 text-sm">No transcript requests found.</div>
        </template>
        <template #loading>
          <div class="text-center py-6 text-gray-500 text-sm">Loading…</div>
        </template>

        <Column field="tracking_code" header="Tracking Code">
          <template #body="{ data }">
            <span class="font-mono text-xs">{{ data.tracking_code }}</span>
          </template>
        </Column>
        <Column field="applicant_name" header="Applicant">
          <template #body="{ data }">
            <div class="text-sm font-medium">{{ data.applicant_name }}</div>
            <div class="text-xs text-gray-500">{{ data.applicant_email }}</div>
            <div v-if="data.matric_no" class="text-xs text-gray-400">Matric: {{ data.matric_no }}</div>
          </template>
        </Column>
        <Column header="Type">
          <template #body="{ data }">
            <div class="text-sm">{{ data.transcriptType?.name ?? '—' }}</div>
            <TranscriptCategoryBadge :category="data.transcriptType?.category" class="mt-1" />
          </template>
        </Column>
        <Column header="Category">
          <template #body="{ data }">
            <span class="text-xs uppercase tracking-wide text-gray-500">{{ formatApplicantCategory(data.applicant_category) }}</span>
          </template>
        </Column>
        <Column header="Status">
          <template #body="{ data }">
            <TranscriptStatusBadge :status="data.status" />
          </template>
        </Column>
        <Column header="Submitted">
          <template #body="{ data }">
            <span class="text-xs text-gray-500">{{ formatDate(data.submitted_at ?? data.created_at) }}</span>
          </template>
        </Column>
        <Column header="Actions" :exportable="false">
          <template #body="{ data }">
            <div class="flex items-center gap-2">
              <Button
                icon="pi pi-eye"
                size="small"
                severity="secondary"
                text
                rounded
                v-tooltip.top="'View'"
                :data-testid="`transcript-view-${data.id}`"
                @click="emit('view', data.id)"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ApplicantCategoryEnum,
  TranscriptRequestStatusEnum,
} from '@/types/composition-functions';
import { useTranscriptRequests, type TranscriptRequestFilters } from '@/composables/transcript/useTranscriptRequests';
import { useTranscriptTypes } from '@/composables/transcript/useTranscriptTypes';

const emit = defineEmits<{ view: [id: string] }>();

const filters = reactive<TranscriptRequestFilters>({
  search: '',
  status: null,
  transcript_type_id: null,
  applicant_category: null,
  first: 15,
  page: 1,
});

const dateFrom = ref<Date | null>(null);
const dateTo = ref<Date | null>(null);

const formatDateToISO = (d: Date | null): string | undefined => {
  if (!d) return undefined;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const currentPage = ref(1);
const perPage = ref(15);

const queryVars = computed<TranscriptRequestFilters>(() => ({
  search: filters.search?.trim() || undefined,
  status: filters.status || undefined,
  transcript_type_id: filters.transcript_type_id || undefined,
  applicant_category: filters.applicant_category || undefined,
  date_from: formatDateToISO(dateFrom.value),
  date_to: formatDateToISO(dateTo.value),
  first: perPage.value,
  page: currentPage.value,
}));

const { requests, paginatorInfo, loading, load, refetch } = useTranscriptRequests(queryVars);
const { types } = useTranscriptTypes();

load();

const total = computed(() => paginatorInfo.value?.total ?? 0);

const statusOptions = [
  { label: 'Pending Payment', value: TranscriptRequestStatusEnum.PendingPayment },
  { label: 'Submitted', value: TranscriptRequestStatusEnum.Submitted },
  { label: 'Processing', value: TranscriptRequestStatusEnum.Processing },
  { label: 'Approved', value: TranscriptRequestStatusEnum.Approved },
  { label: 'Rejected', value: TranscriptRequestStatusEnum.Rejected },
  { label: 'Collected', value: TranscriptRequestStatusEnum.Collected },
];

const categoryOptions = [
  { label: 'Current Student', value: ApplicantCategoryEnum.CurrentStudent },
  { label: 'Without Matric No', value: ApplicantCategoryEnum.UnverifiedApplicant },
  { label: 'With Matric No', value: ApplicantCategoryEnum.MatricRetrieved },
];

const typeOptions = computed(() =>
  (types.value ?? []).map((t) => ({ label: t.name, value: t.id })),
);

let searchTimer: ReturnType<typeof setTimeout> | null = null;

const onFilterChanged = () => {
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    currentPage.value = 1;
    refetch(queryVars.value);
  }, 350);
};

const onPage = (event: { page: number; rows: number }) => {
  currentPage.value = event.page + 1;
  perPage.value = event.rows;
  refetch(queryVars.value);
};

defineExpose({
  refresh: () => refetch(queryVars.value),
});

const formatDate = (iso?: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatApplicantCategory = (cat?: string | null): string => {
  if (!cat) return '—';
  switch (cat) {
  case 'current_student': return 'Current Student';
  case 'unverified_applicant': return 'Without Matric No';
  case 'matric_retrieved': return 'With Matric No';
  default: return cat.replace(/_/g, ' ');
  }
};
</script>
